import { Box, MenuList, Stack } from '@mui/material';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { SlackConnectButton } from './SlackConnectButton';
import { SlackLogo2 } from '../../../icons/generalIcons';
import { HubspotConnectButton } from './HubspotConnectButton';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { IntegrationOption } from './IntegrationsOption';
import { SlackIncludeSandboxesCheckbox } from './SlackIncludeSandboxesCheckbox';
import { HubspotNewLogo, MicrosoftTeamsLogo } from '../../common/connect-sweep-to/integrationIcons';
import { useWebhooksContext } from '../webhooks/WebhooksContext';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { useSelector } from 'react-redux';
import {
  selectCustomIntegrations,
  selectSlackIntegrationStatus,
  selectTeamsIntegrationStatus,
  SlackConnectionStatus,
  TeamsConnectionStatus,
} from '../../../reducers/integrationsReducer';
import { CustomIntegrationsOption } from './CustomIntegrationsOption';
import useCrmOrgPermissions from '../../common/permissions/useCrmOrgPermissions';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import { TeamsConnectButton } from './TeamsConnectButton';
import { useRunOnce } from '../../common/useRunOnce';
import { SearchInput } from '../../common/SearchInput';
import { useCallback, useMemo, useState } from 'react';
import { useHubspot } from '../../pages/canvas-pages/canvas-hubspot-page/useHubspot';
import pluralize from 'pluralize';

const slackHeader = 'Slack';
const hubspotHeader = 'Hubspot';
const microsoftTeamsHeader = 'Microsoft office Teams';

export const IntegrationsTab = () => {
  const { setWebhooks } = useWebhooksContext();
  const { getCustomIntegrations } = useIntegrations();
  const customIntegrations = useSelector(selectCustomIntegrations);
  const permissionStr = 'create:data-integration:custom-integrations';
  const [isAllowedBtnCreate] = useCrmOrgPermissions([permissionStr]);
  const [searchText, setSearchText] = useState('');
  const { multiAccounts } = useFeatureToggle();

  const teamsStatus = useSelector(selectTeamsIntegrationStatus);
  const slackStatus = useSelector(selectSlackIntegrationStatus);
  const { connectedHubspotOrg } = useHubspot();

  const customIntegrationsToDisplay = useMemo(() => {
    return customIntegrations.filter((integration) =>
      integration.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [customIntegrations, searchText]);

  useRunOnce(async () => {
    await getCustomIntegrations();
  });

  const shouldBeDisplayed = useCallback(
    (integrationName: string) => {
      return (
        integrationName.toLowerCase().includes(searchText.toLowerCase()) || searchText.trim() === ''
      );
    },
    [searchText],
  );

  const connectedIntegrationsCount = useMemo(() => {
    const isSlackConnected = slackStatus === SlackConnectionStatus.Connected;
    const isHubspotConnected = !!connectedHubspotOrg;
    const isMicrosoftTeamsConnected = teamsStatus === TeamsConnectionStatus.Connected;

    return (
      (isSlackConnected ? 1 : 0) +
      (isHubspotConnected ? 1 : 0) +
      (isMicrosoftTeamsConnected ? 1 : 0) +
      customIntegrations.length
    );
  }, [customIntegrations, slackStatus, connectedHubspotOrg, teamsStatus]);

  const isListEmpty = useMemo(() => {
    return (
      !customIntegrationsToDisplay.length &&
      !shouldBeDisplayed(slackHeader) &&
      !shouldBeDisplayed(hubspotHeader) &&
      !shouldBeDisplayed(microsoftTeamsHeader)
    );
  }, [customIntegrationsToDisplay, shouldBeDisplayed]);

  return (
    <Stack gap="16px">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h3">Integrations</Typography>

        <RestrictedTooltip
          to={[permissionStr]}
          notAllowedTitle={'To adjust integration settings, please contact your admin.'}
        >
          <Button
            disabled={!isAllowedBtnCreate}
            onClick={() => setWebhooks(true)}
            size="small"
            startIconName="Plus"
            variant="primary"
          >
            {multiAccounts ? 'Connect new' : 'Add new'}
          </Button>
        </RestrictedTooltip>
      </Box>

      {multiAccounts && (
        <Stack
          sx={{
            backgroundColor: colors.grey[100],
            padding: '8px 8px 8px 16px',
            justifyContent: 'space-between',
            flexDirection: 'row',
            width: '100%',
            borderRadius: '8px',
            alignItems: 'center',
          }}
        >
          <Typography variant="body" color={colors.grey[800]}>
            {pluralize('connected integration', connectedIntegrationsCount, true)}
          </Typography>

          <Stack width="320px">
            <SearchInput
              placeholder="Search"
              withFixedMagnifyingGlassIcon
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Stack>
        </Stack>
      )}

      <MenuList
        sx={{
          '&.MuiList-root': {
            padding: 0,
          },
          '.MuiMenuItem-root': {
            padding: 2,
            gap: 2,
            borderBottom: `1px solid ${colors.grey[120]}`,
            margin: 0,
            minHeight: '80px',
            justifyContent: 'flex-start',
          },
        }}
      >
        {isListEmpty && (
          <Typography variant="body" color={colors.grey[500]}>
            No results found
          </Typography>
        )}

        {shouldBeDisplayed(slackHeader) && (
          <IntegrationOption
            name={slackHeader}
            logo={<SlackLogo2 />}
            description="Connect Slack to keep your GTM teams informed with actionable notifications"
            actionButton={<SlackConnectButton />}
            additionalContent={<SlackIncludeSandboxesCheckbox />}
          />
        )}

        {shouldBeDisplayed(hubspotHeader) && (
          <IntegrationOption
            name={hubspotHeader}
            logo={<HubspotNewLogo />}
            description="Connect HubSpot to visualize funnels and manage campaign enrollments"
            actionButton={<HubspotConnectButton />}
          />
        )}

        {shouldBeDisplayed(microsoftTeamsHeader) && (
          <IntegrationOption
            name="Microsoft office Teams"
            logo={<MicrosoftTeamsLogo />}
            description="Connect Teams to keep your GTM teams informed"
            actionButton={<TeamsConnectButton />}
          />
        )}

        {customIntegrationsToDisplay.map((el) => {
          return <CustomIntegrationsOption integration={el} key={el.id} />;
        })}
      </MenuList>
    </Stack>
  );
};
